// src/components/admin/WorkingHoursEditor.jsx
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  query,
  where,
  Timestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { getSalonStaff } from '../../api/salonApi';
import { useSalonLocations } from '../../hooks/useSalonLocations';
import '../../styles/working-hours.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const timeSlots = Array.from({ length: 30 }, (_, i) => 8 + (i * 0.5));
const SLOT_HEIGHT = 50;

function formatSlotTime(slot) {
  const hours = Math.floor(slot);
  const minutes = slot % 1 === 0.5 ? '30' : '00';
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
}

function formatSlotLabel(slot) {
  const hours = Math.floor(slot);
  const minutes = slot % 1 === 0.5 ? '30' : '00';
  const period = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  return `${h12}:${minutes} ${period}`;
}

function getWeekDates(date) {
  const week = [];
  const startOfWeek = new Date(date);
  const day = startOfWeek.getDay();
  const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
  startOfWeek.setDate(diff);
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    week.push(d);
  }
  return week;
}

function formatDateKey(date) {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function calculateWeekHours(weekDatesArr, rosterData) {
  let totalSlots = 0;
  weekDatesArr.forEach(d => {
    const key = formatDateKey(d);
    totalSlots += (rosterData[key] || []).length;
  });
  return totalSlots * 0.5;
}

function calculateMonthHours(date, rosterData) {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let totalSlots = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const key = formatDateKey(new Date(year, month, day));
    totalSlots += (rosterData[key] || []).length;
  }
  return totalSlots * 0.5;
}

function WorkingHoursEditor({ salonId }) {
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const headOfficeLocation = isMultiLocation
    ? (locations.find(l => l.isHeadOffice) || locations[0])
    : null;

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  // Location selection (multi-location + part-time only)
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedLocationName, setSelectedLocationName] = useState('');

  // Form state
  const [employmentType, setEmploymentType] = useState('fulltime');
  const [offDays, setOffDays] = useState([]);
  const [rate, setRate] = useState('');
  const [currency, setCurrency] = useState('SGD');
  const [rateType, setRateType] = useState('monthly');

  // Schedule state
  const [scheduleInterval, setScheduleInterval] = useState('perWeek');
  const [scheduleHoursLimit, setScheduleHoursLimit] = useState('');

  // Roster state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [roster, setRoster] = useState({});
  const [replicateWeeks, setReplicateWeeks] = useState(1);
  const [clearWeeks, setClearWeeks] = useState(1);
  const [existingDocId, setExistingDocId] = useState(null);

  // Hours-validation dialog
  const [showHoursDialog, setShowHoursDialog] = useState(false);
  const [hoursDialogMessage, setHoursDialogMessage] = useState('');
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Overlap error dialog
  const [showOverlapDialog, setShowOverlapDialog] = useState(false);
  const [overlapDetails, setOverlapDetails] = useState([]);

  // Business hours from salon config
  const [regularHours, setRegularHours] = useState([]);
  const [holidayClosures, setHolidayClosures] = useState([]);
  const [customClosures, setCustomClosures] = useState([]);

  useEffect(() => {
    if (salonId) {
      loadStaff();
      loadBusinessHours();
    }
  }, [salonId]);

  // When location pill is switched (part-time + multi-location), reload that location's roster
  useEffect(() => {
    if (selectedStaff && employmentType === 'parttime' && isMultiLocation && selectedLocationId) {
      const staffId = selectedStaff.staffId || selectedStaff.id;
      loadWorkingHoursForLocation(staffId, selectedLocationId);
    }
  }, [selectedLocationId]);

  const loadStaff = async () => {
    try {
      setLoading(true);
      const staffData = await getSalonStaff(salonId);
      setStaff(staffData);
    } catch (error) {
      console.error('Error loading staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessHours = async () => {
    try {
      const salonDoc = await getDoc(doc(db, 'salons', salonId));
      if (salonDoc.exists()) {
        const data = salonDoc.data();
        const bh = data.businessHours;
        if (bh) {
          if (Array.isArray(bh)) {
            setRegularHours(bh);
          } else {
            setRegularHours(bh.regularHours || []);
            setHolidayClosures(bh.holidayClosures || []);
            setCustomClosures(bh.customClosures || []);
          }
        }
      }
    } catch (error) {
      console.error('Error loading business hours:', error);
    }
  };

  const isSlotClosed = (dayName, slotTime, date) => {
    if (regularHours.length > 0) {
      const dayHours = regularHours.find(h => h.day === dayName);
      if (!dayHours) return true;
      if (dayHours.isClosed) return true;
      if (slotTime < dayHours.open || slotTime >= dayHours.close) return true;
    }

    if (date) {
      const dateKey = formatDateKey(date);
      if (holidayClosures.some(h => h && h.date === dateKey)) return true;

      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      for (const closure of customClosures) {
        if (!closure || !closure.startDate || !closure.endDate) continue;
        const startDate = new Date(closure.startDate);
        const endDate = new Date(closure.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        if (checkDate >= startDate && checkDate <= endDate) {
          if (closure.isFullDay) return true;
          if (closure.startTime && closure.endTime) {
            if (slotTime >= closure.startTime && slotTime < closure.endTime) return true;
          }
        }
      }
    }

    return false;
  };

  // Load working hours for a specific location (multi-location + part-time)
  const loadWorkingHoursForLocation = async (staffId, locationId) => {
    try {
      const whRef = collection(db, 'workingHours');
      const whQuery = query(
        whRef,
        where('staffId', '==', staffId),
        where('salonId', '==', salonId),
        where('locationId', '==', locationId)
      );
      const snapshot = await getDocs(whQuery);

      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        const data = docSnap.data();
        setExistingDocId(docSnap.id);
        setScheduleInterval(data.scheduleInterval || 'perWeek');
        setScheduleHoursLimit(data.scheduleHoursLimit ?? '');
        setRoster(data.roster || {});
      } else {
        setExistingDocId(null);
        setScheduleInterval('perWeek');
        setScheduleHoursLimit('');
        setRoster({});
      }
    } catch (error) {
      console.error('Error loading working hours for location:', error);
    }
  };

  // Initial load when staff is selected — loads employment type + rate from any existing doc
  const loadWorkingHours = async (staffId) => {
    try {
      const whRef = collection(db, 'workingHours');
      const whQuery = query(
        whRef,
        where('staffId', '==', staffId),
        where('salonId', '==', salonId)
      );
      const snapshot = await getDocs(whQuery);

      if (!snapshot.empty) {
        const docSnap = snapshot.docs[0];
        const data = docSnap.data();
        setEmploymentType(data.employmentType || 'fulltime');
        // Support legacy string and new array format
        const raw = data.offDay;
        setOffDays(Array.isArray(raw) ? raw : (raw ? [raw] : []));
        setRate(data.rate || '');
        setCurrency(data.currency || 'SGD');
        setRateType(data.rateType || 'monthly');
        setScheduleInterval(data.scheduleInterval || 'perWeek');
        setScheduleHoursLimit(data.scheduleHoursLimit ?? '');

        // For single-location: load roster from this doc
        if (!isMultiLocation) {
          setExistingDocId(docSnap.id);
          setRoster(data.roster || {});
        } else {
          // Multi-location: roster will load when location pill is chosen
          setExistingDocId(null);
          setRoster({});
        }
      } else {
        setExistingDocId(null);
        resetForm();
      }
    } catch (error) {
      console.error('Error loading working hours:', error);
    }
  };

  const resetForm = () => {
    setEmploymentType('fulltime');
    setOffDays([]);
    setRate('');
    setCurrency('SGD');
    setRateType('monthly');
    setScheduleInterval('perWeek');
    setScheduleHoursLimit('');
    setRoster({});
  };

  const handleStaffSelect = (s) => {
    setSelectedStaff(s);
    setSelectedLocationId('');
    setSelectedLocationName('');
    setRoster({});
    setExistingDocId(null);
    setSavedMsg('');
    loadWorkingHours(s.staffId || s.id);
  };

  const handleBack = () => {
    setSelectedStaff(null);
    setSelectedLocationId('');
    setSelectedLocationName('');
    resetForm();
    setExistingDocId(null);
    setSavedMsg('');
  };

  const handleLocationPillClick = (loc) => {
    setSelectedLocationId(loc.locationId);
    setSelectedLocationName(loc.locationName);
    // roster reload is triggered by the useEffect above
  };

  // Check if any other location already has overlapping roster slots for this staff
  const checkRosterOverlaps = async (staffId, currentLocationId, currentRoster) => {
    const whRef = collection(db, 'workingHours');
    const whQuery = query(
      whRef,
      where('staffId', '==', staffId),
      where('salonId', '==', salonId)
    );
    const snapshot = await getDocs(whQuery);

    const conflicts = [];
    snapshot.docs.forEach(d => {
      const data = d.data();
      if (data.locationId === currentLocationId) return;
      if (data.employmentType !== 'parttime') return;

      const otherRoster = data.roster || {};
      const otherLocationName = data.locationName || data.locationId || 'another location';

      Object.entries(currentRoster).forEach(([dateKey, slots]) => {
        if (!slots || slots.length === 0) return;
        const otherSlots = otherRoster[dateKey] || [];
        const overlapping = slots.filter(s => otherSlots.includes(s));
        if (overlapping.length > 0) {
          conflicts.push({
            date: dateKey,
            locationName: otherLocationName,
            times: overlapping
          });
        }
      });
    });

    return conflicts;
  };

  const weekDates = getWeekDates(currentDate);

  const handleSlotClick = (dateKey, slotTime) => {
    setRoster(prev => {
      const daySlots = prev[dateKey] || [];
      if (daySlots.includes(slotTime)) {
        const filtered = daySlots.filter(s => s !== slotTime);
        const updated = { ...prev };
        if (filtered.length === 0) {
          delete updated[dateKey];
        } else {
          updated[dateKey] = filtered;
        }
        return updated;
      } else {
        return { ...prev, [dateKey]: [...daySlots, slotTime].sort() };
      }
    });
  };

  const handleReplicateWeeks = () => {
    const currentWeekSlots = {};
    weekDates.forEach(d => {
      const key = formatDateKey(d);
      const dayOfWeek = d.getDay();
      currentWeekSlots[dayOfWeek] = roster[key] || [];
    });

    setRoster(prev => {
      const updated = { ...prev };
      for (let w = 1; w <= replicateWeeks; w++) {
        weekDates.forEach(d => {
          const futureDate = new Date(d);
          futureDate.setDate(futureDate.getDate() + (w * 7));
          const futureKey = formatDateKey(futureDate);
          const dayOfWeek = d.getDay();
          if (currentWeekSlots[dayOfWeek].length > 0) {
            updated[futureKey] = [...currentWeekSlots[dayOfWeek]];
          } else {
            delete updated[futureKey];
          }
        });
      }
      return updated;
    });

    setSavedMsg(`Replicated to next ${replicateWeeks} week(s)`);
    setTimeout(() => setSavedMsg(''), 3000);
  };

  const handleClearWeeks = () => {
    setRoster(prev => {
      const updated = { ...prev };
      for (let w = 1; w <= clearWeeks; w++) {
        weekDates.forEach(d => {
          const futureDate = new Date(d);
          futureDate.setDate(futureDate.getDate() + (w * 7));
          const futureKey = formatDateKey(futureDate);
          delete updated[futureKey];
        });
      }
      return updated;
    });

    setSavedMsg(`Cleared next ${clearWeeks} week(s)`);
    setTimeout(() => setSavedMsg(''), 3000);
  };

  const handleClearAllWeeks = () => {
    setRoster({});
    setSavedMsg('Cleared all weeks');
    setTimeout(() => setSavedMsg(''), 3000);
  };

  // ---- Hours-limit validation on week navigation ----

  const checkHoursAndNavigate = (navigationFn) => {
    if (scheduleInterval === 'variable' || !scheduleHoursLimit) {
      navigationFn();
      return;
    }

    const limit = parseFloat(scheduleHoursLimit);
    if (isNaN(limit) || limit <= 0) {
      navigationFn();
      return;
    }

    let currentHours;
    let intervalLabel;

    if (scheduleInterval === 'perWeek') {
      currentHours = calculateWeekHours(weekDates, roster);
      intervalLabel = 'per week';
    } else {
      currentHours = calculateMonthHours(currentDate, roster);
      intervalLabel = 'per month';
    }

    if (currentHours > limit) {
      setHoursDialogMessage(
        `You have rostered ${currentHours} hours ${intervalLabel}, which exceeds the defined limit of ${limit} hours ${intervalLabel} for this staff.`
      );
      setShowHoursDialog(true);
      setPendingNavigation(() => navigationFn);
    } else if (currentHours < limit) {
      setHoursDialogMessage(
        `You have rostered ${currentHours} hours ${intervalLabel}, which is less than the defined limit of ${limit} hours ${intervalLabel} for this staff.`
      );
      setShowHoursDialog(true);
      setPendingNavigation(() => navigationFn);
    } else {
      navigationFn();
    }
  };

  const navigateWeek = (direction) => {
    const doNav = () => {
      setCurrentDate(prev => {
        const d = new Date(prev);
        d.setDate(d.getDate() + (direction * 7));
        return d;
      });
    };
    checkHoursAndNavigate(doNav);
  };

  const goToToday = () => {
    const doNav = () => setCurrentDate(new Date());
    checkHoursAndNavigate(doNav);
  };

  const handleDialogAcknowledge = () => {
    setShowHoursDialog(false);
    setHoursDialogMessage('');
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleDialogCancel = () => {
    setShowHoursDialog(false);
    setHoursDialogMessage('');
    setPendingNavigation(null);
  };

  // ---- Save ----

  const handleSave = async () => {
    if (!selectedStaff) return;

    const staffId = selectedStaff.staffId || selectedStaff.id;
    const isPartTime = employmentType === 'parttime';

    // Multi-location + part-time: require a location to be selected
    if (isMultiLocation && isPartTime && !selectedLocationId) {
      setSavedMsg('Please select a location before saving working hours.');
      setTimeout(() => setSavedMsg(''), 4000);
      return;
    }

    setSaving(true);
    setSavedMsg('');

    try {
      // Overlap check for multi-location part-time staff
      if (isMultiLocation && isPartTime && selectedLocationId && Object.keys(roster).length > 0) {
        const conflicts = await checkRosterOverlaps(staffId, selectedLocationId, roster);
        if (conflicts.length > 0) {
          setOverlapDetails(conflicts);
          setShowOverlapDialog(true);
          setSaving(false);
          return;
        }
      }

      const whData = {
        staffId,
        salonId,
        staffName: selectedStaff.name || '',
        employmentType,
        contractStartDate: null,
        contractEndDate: null,
        offDay: employmentType === 'fulltime' ? (offDays.length > 0 ? offDays : null) : null,
        rate: rate ? parseFloat(rate) : null,
        currency,
        rateType,
        scheduleInterval: isPartTime ? scheduleInterval : null,
        scheduleHoursLimit: (isPartTime && scheduleInterval !== 'variable' && scheduleHoursLimit)
          ? parseFloat(scheduleHoursLimit) : null,
        roster: isPartTime ? roster : {},
        // Single-location: delete any stale location fields from prior migration.
        // Multi-location fulltime: always pin to head office.
        // Multi-location parttime: use the selected location pill.
        locationId: !isMultiLocation
          ? deleteField()
          : isPartTime
            ? selectedLocationId
            : headOfficeLocation?.locationId ?? deleteField(),
        locationName: !isMultiLocation
          ? deleteField()
          : isPartTime
            ? selectedLocationName
            : headOfficeLocation?.locationName ?? deleteField(),
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Determine document ID
      let docId;
      if (isMultiLocation && isPartTime && selectedLocationId) {
        docId = `${salonId}_${staffId}_${selectedLocationId}`;
      } else {
        docId = existingDocId || `${salonId}_${staffId}`;
      }

      if (!existingDocId) {
        whData.createdAt = Timestamp.fromDate(new Date());
      }

      await setDoc(doc(db, 'workingHours', docId), whData, { merge: true });
      setExistingDocId(docId);

      setSavedMsg('Working hours saved successfully!');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (error) {
      console.error('Error saving working hours:', error);
      setSavedMsg('Error saving. Please try again.');
      setTimeout(() => setSavedMsg(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  // ---- Computed ----

  const showSchedule = employmentType === 'parttime';
  const rosterLocked = isMultiLocation && showSchedule && !selectedLocationId;

  const currentWeekHours = calculateWeekHours(weekDates, roster);
  const currentMonthHours = calculateMonthHours(currentDate, roster);

  if (loading && staff.length === 0) {
    return <div className="loading">Loading staff...</div>;
  }

  // ==================== Detail view ====================
  if (selectedStaff) {
    return (
      <div className="working-hours-editor">
        <button className="wh-back-button" onClick={handleBack}>
          Back to Staff List
        </button>

        <h2>Working Hours - {selectedStaff.name}</h2>

        {savedMsg && <div className="wh-save-message">{savedMsg}</div>}

        {/* Section A: Employment Type */}
        <div className="wh-section">
          <h3>Employment Type</h3>
          <div className="employment-type-selector">
            {[
              { value: 'fulltime', label: 'Full Time' },
              { value: 'parttime', label: 'Part Time' }
            ].map(opt => (
              <label key={opt.value} className={`employment-radio ${employmentType === opt.value ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="employmentType"
                  value={opt.value}
                  checked={employmentType === opt.value}
                  onChange={(e) => {
                    setEmploymentType(e.target.value);
                    if (e.target.value === 'fulltime') {
                      setSelectedLocationId('');
                      setSelectedLocationName('');
                      setRoster({});
                    }
                  }}
                />
                {opt.label}
              </label>
            ))}
          </div>

          {employmentType === 'fulltime' && (
            <div className="wh-form-group wh-off-day">
              <label>Off Day(s)</label>
              <div className="wh-off-day-checkboxes">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                  <label key={day} className="wh-off-day-option">
                    <input
                      type="checkbox"
                      checked={offDays.includes(day)}
                      onChange={(e) => {
                        setOffDays(prev =>
                          e.target.checked ? [...prev, day] : prev.filter(d => d !== day)
                        );
                      }}
                    />
                    {day}
                  </label>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Section B: Rate Configuration */}
        <div className="wh-section">
          <h3>Rate Configuration</h3>
          <div className="rate-config">
            <div className="wh-form-group">
              <label>Rate</label>
              <input
                type="number"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div className="wh-form-group">
              <label>Currency</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                <option value="SGD">SGD</option>
                <option value="MYR">MYR</option>
              </select>
            </div>
            <div className="wh-form-group">
              <label>Rate Type</label>
              <select value={rateType} onChange={(e) => setRateType(e.target.value)}>
                <option value="hourly">Per Hour</option>
                <option value="weekly">Per Week</option>
                <option value="monthly">Per Month</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section C: Location Pills (multi-location + part-time only) */}
        {showSchedule && isMultiLocation && (
          <div className="wh-section">
            <h3>Location</h3>
            <p className="wh-location-hint">
              Select the location these working hours apply to. Each location must be configured separately.
            </p>
            <div className="wh-location-pills">
              {locations.map(loc => (
                <button
                  key={loc.locationId}
                  className={`wh-location-pill${selectedLocationId === loc.locationId ? ' wh-location-pill--active' : ''}`}
                  onClick={() => handleLocationPillClick(loc)}
                  type="button"
                >
                  {loc.locationName}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Section D: Schedule (Part Time only) */}
        {showSchedule && (
          <div className={`wh-section${rosterLocked ? ' wh-section--locked' : ''}`}>
            <h3>Schedule</h3>
            {rosterLocked && (
              <p className="wh-location-required-hint">
                Please select a location above to configure the schedule for that location.
              </p>
            )}
            {!rosterLocked && (
              <div className="schedule-config">
                <div className="schedule-interval-row">
                  <div className="wh-form-group">
                    <label>Schedule Interval</label>
                    <select
                      value={scheduleInterval}
                      onChange={(e) => setScheduleInterval(e.target.value)}
                    >
                      <option value="perWeek">Per Week</option>
                      <option value="perMonth">Per Month</option>
                      <option value="variable">Variable</option>
                    </select>
                  </div>

                  {scheduleInterval !== 'variable' && (
                    <div className="wh-form-group">
                      <label>
                        Number of Hours {scheduleInterval === 'perWeek' ? 'per Week' : 'per Month'}
                      </label>
                      <input
                        type="number"
                        value={scheduleHoursLimit}
                        onChange={(e) => setScheduleHoursLimit(e.target.value)}
                        placeholder="0"
                        min="0"
                        step="0.5"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Section E: Rostering Calendar (Part Time only) */}
        {showSchedule && (
          <div className={`wh-section${rosterLocked ? ' wh-section--locked' : ''}`}>
            <h3>Rostering Calendar</h3>

            {rosterLocked ? (
              <p className="wh-location-required-hint">
                Please select a location above to view and edit the roster calendar for that location.
              </p>
            ) : (
              <>
                {/* Location badge */}
                {isMultiLocation && selectedLocationName && (
                  <div className="wh-active-location-badge">
                    Editing roster for: <strong>{selectedLocationName}</strong>
                  </div>
                )}

                {/* Live hours tally */}
                {scheduleInterval !== 'variable' && scheduleHoursLimit && (
                  <div className="roster-hours-tally">
                    {scheduleInterval === 'perWeek' ? (
                      <span>
                        This week: <strong>{currentWeekHours}</strong> / {scheduleHoursLimit} hours
                      </span>
                    ) : (
                      <span>
                        This month: <strong>{currentMonthHours}</strong> / {scheduleHoursLimit} hours
                      </span>
                    )}
                  </div>
                )}

                {/* Week navigation */}
                <div className="roster-week-nav">
                  <button onClick={() => navigateWeek(-1)}>Previous Week</button>
                  <button onClick={goToToday}>Today</button>
                  <button onClick={() => navigateWeek(1)}>Next Week</button>
                </div>

                <div className="roster-week-label">
                  {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' - '}
                  {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>

                {/* Roster grid */}
                <div className="roster-calendar">
                  <div className="roster-header">
                    <div className="roster-time-label">Time</div>
                    {weekDates.map((d, i) => (
                      <div key={i} className="roster-day-header">
                        <span className="roster-day-name">{DAYS_OF_WEEK[i].substring(0, 3)}</span>
                        <span className="roster-day-date">{d.getDate()}</span>
                      </div>
                    ))}
                  </div>

                  <div className="roster-body">
                    {timeSlots.map(slot => {
                      const slotTime = formatSlotTime(slot);
                      return (
                        <div key={slot} className="roster-row" style={{ height: `${SLOT_HEIGHT}px` }}>
                          <div className="roster-time-cell">
                            {slot % 1 === 0 ? formatSlotLabel(slot) : ''}
                          </div>
                          {weekDates.map((d, i) => {
                            const dateKey = formatDateKey(d);
                            const isWorking = (roster[dateKey] || []).includes(slotTime);
                            const isClosed = isSlotClosed(DAYS_OF_WEEK[i], slotTime, d);
                            let slotClass = 'roster-time-slot';
                            if (isClosed) {
                              slotClass += ' closed';
                            } else if (isWorking) {
                              slotClass += ' working';
                            } else {
                              slotClass += ' non-working';
                            }
                            return (
                              <div
                                key={i}
                                className={slotClass}
                                onClick={() => !isClosed && handleSlotClick(dateKey, slotTime)}
                                title={`${formatSlotLabel(slot)} - ${DAYS_OF_WEEK[i]}${isClosed ? ' (Salon Closed)' : ''}`}
                              />
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Replicate & Clear controls */}
                <div className="replicate-controls">
                  <label>Replicate to next</label>
                  <input
                    type="number"
                    value={replicateWeeks}
                    onChange={(e) => setReplicateWeeks(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    max="52"
                    style={{ width: '60px' }}
                  />
                  <span>week(s)</span>
                  <button onClick={handleReplicateWeeks}>Apply</button>
                </div>
                <div className="replicate-controls">
                  <label>Clear for next</label>
                  <input
                    type="number"
                    value={clearWeeks}
                    onChange={(e) => setClearWeeks(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    max="52"
                    style={{ width: '60px' }}
                  />
                  <span>week(s)</span>
                  <button className="wh-clear-button" onClick={handleClearWeeks}>Clear</button>
                  <button className="wh-clear-all-button" onClick={handleClearAllWeeks}>Clear All Weeks</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Save button */}
        <div className="wh-form-actions">
          <button className="wh-cancel-button" onClick={handleBack}>Cancel</button>
          <button className="wh-save-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Working Hours'}
          </button>
        </div>

        {/* Hours-limit dialog */}
        {showHoursDialog && (
          <div className="wh-dialog-overlay" onClick={handleDialogCancel}>
            <div className="wh-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="wh-dialog-header">
                <h3>Hours Mismatch</h3>
              </div>
              <div className="wh-dialog-body">
                <p>{hoursDialogMessage}</p>
              </div>
              <div className="wh-dialog-actions">
                <button className="wh-dialog-cancel" onClick={handleDialogCancel}>
                  Go Back
                </button>
                <button className="wh-dialog-proceed" onClick={handleDialogAcknowledge}>
                  Proceed Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Roster overlap error dialog */}
        {showOverlapDialog && (
          <div className="wh-dialog-overlay" onClick={() => setShowOverlapDialog(false)}>
            <div className="wh-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="wh-dialog-header">
                <h3>Roster Conflict Detected</h3>
              </div>
              <div className="wh-dialog-body">
                <p>
                  <strong>{selectedStaff.name}</strong> is already rostered at another location
                  during the following times. A staff member cannot be at two locations simultaneously.
                </p>
                <ul className="wh-conflict-list">
                  {overlapDetails.map((c, idx) => (
                    <li key={idx}>
                      <strong>{c.date}</strong> at {c.locationName}:{' '}
                      {c.times.join(', ')}
                    </li>
                  ))}
                </ul>
                <p>Please adjust the roster to remove these conflicts before saving.</p>
              </div>
              <div className="wh-dialog-actions">
                <button className="wh-dialog-cancel" onClick={() => setShowOverlapDialog(false)}>
                  Go Back and Fix
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== Staff list view ====================
  return (
    <div className="working-hours-editor">
      <h2>Working Hours</h2>
      <p className="wh-description">
        Select a staff member to configure their employment type, rate, and working schedule.
      </p>

      {staff.length === 0 ? (
        <div className="wh-no-staff">
          <p>No staff found. Please add staff in the Core module first.</p>
        </div>
      ) : (
        <div className="wh-staff-list">
          {staff.map(s => (
            <div
              key={s.staffId || s.id}
              className={`wh-staff-card ${!s.isActive ? 'inactive' : ''}`}
              onClick={() => handleStaffSelect(s)}
            >
              <div className="wh-staff-photo">
                {s.photoURL ? (
                  <img
                    src={s.photoURL}
                    alt={s.name}
                    onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <div className="wh-photo-placeholder" style={s.photoURL ? { display: 'none' } : undefined}>
                  {s.name ? s.name.charAt(0).toUpperCase() : '?'}
                </div>
              </div>
              <div className="wh-staff-info">
                <h3>{s.name}{!s.isActive && <span className="wh-inactive-badge">Inactive</span>}</h3>
                {s.title && <p className="wh-staff-title">{s.title}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WorkingHoursEditor;
